/* eslint no-underscore-dangle: ["error", { "allow": ["_id"] }] */
import React, { useEffect, useState } from 'react';
import { Box, Grid, Card, CardHeader, CardContent, Button } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import Alert from '@mui/material/Alert';
import 'react-datepicker/dist/react-datepicker.css';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardAction from '../../store/actions/DashboardAction';
import InvoiceChart from './invoiceChart';
import RecentActivities from './recentActivities';
import InvoiceAmountCard from './invoiceAmount';
import RecentTransaction from './recentTransaction';
import RecentInvoice from './recentInvoice';
import GateWayInvoiceAmount from './gatewayInvoiceAmount';
import OverAllAmount from './overAllAmount';
import FilterDropdown from './filterDashboard';
import MultiRangeProgressBar from './totalReceivables';
import YearlyAmount from './yearlyAmount';
import useHasPermission from '../../utils/useHasPermission';
import Loader from '../../utils/errorPages/Loader';
import { auditTrail, paymentRequest, transaction, index } from '../../utils/constants';

function Dashboard() {
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [currencies, setCurrencies] = useState(null);
  const [department, setDepartment] = useState(null);
  const [client, setClient] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [currencyName, setCurrencyName] = useState('');
  const [invoiceCountData, setInvoiceCountData] = useState([]);
  const [gatewaydAmount, setGatewaydAmount] = useState([]);
  const [overAllAmount, setOverAllAmount] = useState([]);
  const dispatch = useDispatch();
  const handleSubmit = () => {
    const rangeDate = {
      startDate,
      endDate
    };
    if (currencies?._id || department?._id || client?._id || rangeDate) {
      dispatch(DashboardAction.getAll(client?._id, department?._id, currencies?._id, rangeDate));
    }
  };

  const { dashItem, isLoading } = useSelector((state) => state.dashboard);
  const { noUserPermissions } = useSelector((state) => state.rolePermission);

  const hasPaymentRequestPermission = useHasPermission(paymentRequest, index);
  const hasTransactionPermission = useHasPermission(transaction, index);
  const hasAuditTrailPermission = useHasPermission(auditTrail, index);

  const {
    totalInvoiceAmount,
    totalReceivables,
    invoiceYearlySummary,
    recentTransaction,
    recentInvoice,
    recentActivities
  } = dashItem || {};

  useEffect(() => {
    dispatch(DashboardAction.getAll());
  }, [dispatch]);

  useEffect(() => {
    if (dashItem && dashItem.invoiceCount) {
      // Remove 'total_count' property from invoiceCount object
      const { total_count: __, ...invoiceCountWithoutTotal } = dashItem.invoiceCount;
      const invoiceCountTransformed = Object.entries(invoiceCountWithoutTotal).map(
        ([key, value]) => ({
          name: key.replace(/_/g, ' ').toUpperCase(),
          y: value
        })
      );
      setInvoiceCountData(invoiceCountTransformed);
    }

    if (dashItem && dashItem.invoiceOverAllAmount) {
      // Remove 'currency' property from gatewayReceivedAmount object
      const { currency: __, ...overAllAmountWithoutTCurrency } = dashItem.invoiceOverAllAmount;
      const overAllAmountTransformed = Object.entries(overAllAmountWithoutTCurrency).map(
        ([key, value]) => ({
          name: key.replace(/_/g, ' ').toUpperCase(),
          y: parseFloat(value)
        })
      );
      setOverAllAmount(overAllAmountTransformed);
    }

    if (dashItem && dashItem.gatewayReceivedAmount) {
      // Remove 'currency' property from gatewayReceivedAmount object
      const { currency: __, ...gatewaydAmountCurrency } = dashItem.gatewayReceivedAmount;
      const gatewaydAmountTransformed = Object.entries(gatewaydAmountCurrency).map(
        ([key, value]) => ({
          name: key.replace(/_/g, ' ').toUpperCase(),
          y: parseFloat(value)
        })
      );
      setGatewaydAmount(gatewaydAmountTransformed);
    }

    if (dashItem && dashItem?.currency) {
      setCurrencyName(dashItem?.currency);
    }
  }, [dashItem]);

  if (noUserPermissions) {
    return (
      <Box>
        {isLoading ? <Loader /> : ''}
        <Card elevation={0} sx={{ background: 'none' }}>
          <CardHeader
            className=""
            sx={{
              pl: 0,
              py: 2,
              '.MuiTypography-root': {
                fontSize: '16px',
                lineHeight: '20px',
                color: '#2f2b3d',
                fontWeight: '700',
                textAlign: 'left'
              }
            }}
            title="Dashboard"
          />
          <CardContent>
            <Alert severity="error">
              You don&apos;t have permission to access this page! Please Contact Portal
              Administrator.
            </Alert>
          </CardContent>
        </Card>
      </Box>
    );
  }
  return (
    <Box>
      {isLoading ? <Loader /> : ''}
      <Card elevation={0} sx={{ background: 'none' }}>
        <CardHeader
          className=""
          sx={{
            pl: 0,
            py: 2,
            '.MuiTypography-root': {
              fontSize: '16px',
              lineHeight: '20px',
              color: '#2f2b3d',
              fontWeight: '700',
              textAlign: 'left'
            }
          }}
          title="Dashboard"
          action={
            (hasPaymentRequestPermission || hasTransactionPermission) && (
              <Box
                sx={{
                  position: 'relative',
                  display: 'inline-flex',
                  alignItems: 'center'
                }}
              >
                {/* Red Dot */}
                {(client || department || currencies || (startDate && endDate)) && (
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: 'red',
                      position: 'absolute',
                      left: -8,
                      top: '23%',
                      transform: 'translateY(-50%)'
                    }}
                  />
                )}
                <Button
                  variant="text"
                  sx={{
                    px: '0 !important',
                    minWidth: 'auto !important',
                    '& > *:first-of-type': {
                      '.MuiSvgIcon-root': {
                        fontSize: '26px'
                      }
                    }
                  }}
                  startIcon={<FilterAltOutlinedIcon />}
                  onClick={() => {
                    setFilterOpen(true);
                  }}
                  aria-label="Filter"
                >
                  Filters
                </Button>
              </Box>
            )
          }
        />
      </Card>
      {hasPaymentRequestPermission && (
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} xl={12}>
              <InvoiceAmountCard data={totalInvoiceAmount} />
            </Grid>

            {invoiceCountData && (
              <Grid item xs={6} xl={6} className="cardWrapper">
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <InvoiceChart data={invoiceCountData} />
                  </CardContent>
                </Card>
              </Grid>
            )}

            {gatewaydAmount && (
              <Grid item xs={6} xl={6} className="cardWrapper">
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <GateWayInvoiceAmount data={gatewaydAmount} currency={currencyName} />
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {totalReceivables && (
            <MultiRangeProgressBar data={totalReceivables} currency={currencyName} />
          )}

          {invoiceYearlySummary && (
            <Grid container spacing={2}>
              <Grid item xs={12} xl={12} className="cardWrapper">
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <YearlyAmount data={invoiceYearlySummary} currency={currencyName} />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      <Box className="cardWrapper">
        <Grid container columnSpacing={2} sx={{ height: '100%' }}>
          {hasPaymentRequestPermission && (
            <Grid
              item
              xs={hasAuditTrailPermission ? 6 : 12}
              xl={hasAuditTrailPermission && hasTransactionPermission ? 6 : 8}
            >
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ height: '100%', p: '20px 24px 20px 8px!important' }}>
                  {overAllAmount && overAllAmount !== 'undefined ' ? (
                    <OverAllAmount data={overAllAmount} currency={currencyName} />
                  ) : (
                    ''
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
          {useHasPermission(auditTrail, index) && (
            <Grid
              item
              xs={hasPaymentRequestPermission ? 6 : 12}
              xl={hasPaymentRequestPermission ? 6 : 8}
            >
              <Card>
                <CardHeader
                  title="Recent Activities"
                  action={
                    <Button
                      variant="text"
                      size="small"
                      sx={{ p: '0!important', minWidth: 'auto!important' }}
                      endIcon={<ChevronRightIcon />}
                      component={Link}
                      to="/audit-trial"
                      aria-label="Filter"
                    >
                      Show All
                    </Button>
                  }
                />
                <CardContent sx={{ height: '100%', p: '20px 24px 20px 8px!important' }}>
                  {recentActivities && recentActivities !== 'undefined ' ? (
                    <RecentActivities data={recentActivities} />
                  ) : (
                    ''
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
          {hasTransactionPermission && (
            <Grid item xs={12} xl={6}>
              <Card sx={{ mt: 2 }}>
                <CardContent sx={{ height: '100%' }}>
                  {recentTransaction && recentTransaction !== 'undefined ' ? (
                    <RecentTransaction data={recentTransaction} />
                  ) : (
                    ''
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
          {hasPaymentRequestPermission && (
            <Grid item xs={12} xl={6}>
              <Card sx={{ mt: 2 }}>
                <CardContent sx={{ height: '100%' }}>
                  {recentInvoice && recentInvoice !== 'undefined ' ? (
                    <RecentInvoice data={recentInvoice} />
                  ) : (
                    ''
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
      {/* <Box className="cardWrapper">
        <Card sx={{ mt: 2 }}>
          <CardContent sx={{ height: '100%' }}>
            <Grid container columnSpacing={2} alignItems="flex-start" sx={{ height: '100%' }}>
          
            </Grid>
          </CardContent>
        </Card>
      </Box> */}
      {filterOpen === true ? (
        <FilterDropdown
          setCurrencies={setCurrencies}
          currencies={currencies}
          setDepartment={setDepartment}
          department={department}
          setClient={setClient}
          client={client}
          setDateRange={setDateRange}
          dateRange={dateRange}
          startDate={startDate}
          endDate={endDate}
          handleSubmit={handleSubmit}
          openmodal={filterOpen}
          onCloseModal={() => setFilterOpen(false)}
        />
      ) : (
        ''
      )}
    </Box>
  );
}

export default Dashboard;
